// C:\Users\server\Desktop\FRONTEND\app-user-front\src\pages\Dashboard\requestmodel.js
// ==================== Imports ====================
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Spinner, Row, Col, Alert } from 'react-bootstrap';
import axios from 'axios';
import CONFIG from '../../config'; // <-- uses your runtime config only

// ---------------- REQUEST MODAL COMPONENT ----------------
const RequestModal = ({ show, onClose, product, onRequestSubmit }) => {
  const [email, setEmail] = useState('');
  const [productName, setProductName] = useState(''); // now auto-filled from user's products
  const [selectedProductId, setSelectedProductId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // track login state
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // logged-in user's products
  const [userProducts, setUserProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');

  // API base URL — strict runtime config only (no fallbacks)
  const API_BASE_URL = CONFIG?.API_URL || '';

  // ---------------- Helper: get logged-in user's email (best-effort) ----------------
  function getLoggedInEmail() {
    try {
      if (window && window.__USER__ && window.__USER__.email) return String(window.__USER__.email);
      if (window && window.__auth && window.__auth.user && window.__auth.user.email) return String(window.__auth.user.email);
      const tryKeys = ['user', 'auth_user', 'profile', 'auth', 'currentUser', 'current_user'];
      for (const key of tryKeys) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.email) return String(parsed.email);
          if (parsed && parsed.user && parsed.user.email) return String(parsed.user.email);
        } catch (e) {
          if (typeof raw === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return raw;
        }
      }
      const direct = localStorage.getItem('userEmail') || localStorage.getItem('email');
      if (direct && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(direct)) return direct;
    } catch (err) {
      // ignore
    }
    return '';
  }

  // Reset form when modal opens/closes or product changes
  useEffect(() => {
    if (show) {
      const logged = getLoggedInEmail();
      if (logged) {
        setEmail(logged);
        setIsLoggedIn(true);
      } else {
        setEmail('');
        setIsLoggedIn(false);
      }
      setProductName('');
      setSelectedProductId('');
      setError('');
      setMessage('');
      setShowSuccess(false);
      setUserProducts([]);
      setProductsError('');
      setProductsLoading(false);
    }
  }, [show, product]);

  // Fetch logged-in user's products when modal is open & user is logged in
  useEffect(() => {
    if (!show || !isLoggedIn || !email || !API_BASE_URL) {
      return;
    }

    const fetchUserProducts = async () => {
      try {
        setProductsLoading(true);
        setProductsError('');

        // same endpoint, backend agar email param ignore bhi kare to hum frontend pe filter karenge
        const endpoint = `${API_BASE_URL.replace(/\/+$/, '')}/products?email=${encodeURIComponent(email)}`;
        const res = await axios.get(endpoint, {
          headers: { Accept: 'application/json' },
          timeout: 10000,
        });

        let list = [];
        const data = res.data;

        if (Array.isArray(data)) {
          list = data;
        } else if (Array.isArray(data?.products)) {
          list = data.products;
        } else if (Array.isArray(data?.data)) {
          list = data.data;
        }

        // ✅ Only:
        // 1) email == logged-in user
        // 2) approval_status === 'accepted'
        const filtered = list.filter((p) => {
          const productEmail = (p.email || p.user_email || p.owner_email || '').toLowerCase();
          const userEmail = email.toLowerCase();
          const approved = String(p.approval_status || '').toLowerCase() === 'accepted';
          return productEmail && userEmail && productEmail === userEmail && approved;
        });

        setUserProducts(filtered);

        // Auto-select first product (if any)
        if (filtered.length > 0) {
          const first = filtered[0];
          setSelectedProductId(String(first.id));
          setProductName(first.name || '');
        } else {
          setSelectedProductId('');
          setProductName('');
        }
      } catch (err) {
        console.error('Failed to load user products:', err);
        setProductsError('Could not load your products. Please try again later.');
        setUserProducts([]);
        setSelectedProductId('');
        setProductName('');
      } finally {
        setProductsLoading(false);
      }
    };

    fetchUserProducts();
  }, [show, isLoggedIn, email, API_BASE_URL]);

  // Handle success message and auto-close
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        handleCleanupAndClose();
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // Cleanup and close function
  const handleCleanupAndClose = useCallback(() => {
    setEmail('');
    setProductName('');
    setSelectedProductId('');
    setError('');
    setMessage('');
    setShowSuccess(false);
    setIsSubmitting(false);
    setIsLoggedIn(false);
    setUserProducts([]);
    setProductsError('');
    setProductsLoading(false);
    onClose(); // ✅ Always call onClose
  }, [onClose]);

  const handleClose = () => {
    handleCleanupAndClose();
  };

  // Validation function
  const validateForm = () => {
    if (!email) {
      return 'Please enter your email address';
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      return 'Please enter a valid email address';
    }

    if (!selectedProductId) {
      return 'Please select one of your products to swap';
    }

    if (!product || !product.id) {
      return 'Product information is missing';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setShowSuccess(false);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    // EXTRA SAFETY: block if user trying to request own product
    const ownerEmail = product
      ? (product.user_email || product.owner_email || product.email || '')
      : '';
    if (
      isLoggedIn &&
      email &&
      ownerEmail &&
      email.toLowerCase() === ownerEmail.toLowerCase()
    ) {
      setError('You cannot request your own product.');
      setTimeout(() => {
        handleCleanupAndClose();
      }, 2000);
      return;
    }

    if (!API_BASE_URL) {
      setError('API base URL not configured. Please set CONFIG.API_URL in public/config.js');
      setTimeout(() => {
        handleCleanupAndClose();
      }, 3000);
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedProduct = userProducts.find(
        (p) => String(p.id) === String(selectedProductId)
      );

      const offerName = selectedProduct?.name || productName || 'My Product';

      const swapRequestData = {
        request_user_email: email.trim(),
        request_user_product_id: selectedProduct ? selectedProduct.id : null,
        request_user_product_name: offerName,
        requested_product_name: product.name,
        requested_product_id: product.id,
        product_owner_email:
          product.user_email || product.owner_email || product.email || 'admin@example.com',
        status: 'pending',
        message: `I would like to swap "${offerName}" for "${product.name}"`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      console.log('Sending swap request data:', swapRequestData);

      const endpoint = `${API_BASE_URL.replace(/\/+$/, '')}/swap-requests`;

      console.log('Request endpoint:', endpoint);

      const response = await axios.post(endpoint, swapRequestData, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        timeout: 10000,
      });

      console.log('API Response:', response);

      if (response.data && response.data.success) {
        setMessage('Swap request submitted successfully! 🎉');
        setShowSuccess(true);
      } else {
        throw new Error(response.data?.message || 'Failed to save swap request');
      }
    } catch (error) {
      console.error('Request failed:', error);

      let errorMessage = 'Failed to submit request. Please try again.';

      if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data;

        if (status === 404) {
          errorMessage = 'API endpoint not found. Please check the server configuration.';
        } else if (status === 422) {
          errorMessage =
            responseData?.message ||
            (responseData?.errors
              ? Object.values(responseData.errors).flat().join(' ')
              : '') ||
            'Please check your input data';
        } else if (status === 500) {
          errorMessage =
            "Your swap request has been saved in our database. The product owner will be notified and you'll receive an email confirmation shortly.";
        } else if (status === 409) {
          errorMessage = 'A swap request for this product already exists.';
        } else {
          errorMessage = responseData?.message || `Error (${status}). Please try again.`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection and server status.';
      } else if (error.message && error.message.includes('timeout')) {
        errorMessage = 'Request timeout. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);

      setTimeout(() => {
        handleCleanupAndClose();
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  // compute owner email + self-product flag for UI
  const ownerEmail = product
    ? (product.user_email || product.owner_email || product.email || '')
    : '';
  const isOwnProduct =
    isLoggedIn && email && ownerEmail && email.toLowerCase() === ownerEmail.toLowerCase();

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? undefined : handleClose}
      centered
      size="lg"
      aria-labelledby="swap-request-modal-title"
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
    >
      <Modal.Header closeButton={!isSubmitting} style={styles.modalHeader}>
        <Modal.Title id="swap-request-modal-title" style={styles.modalTitle}>
          <i className="ri-exchange-line me-2" aria-hidden="true"></i>
          Request Product Swap
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={styles.modalBody}>
        {/* Success Message */}
        {showSuccess && (
          <Alert variant="success" style={styles.successAlert}>
            <div style={styles.successContent}>
              <i
                className="ri-checkbox-circle-line me-2"
                style={{ fontSize: '1.5rem' }}
                aria-hidden="true"
              ></i>
              <div>
                <h6 style={styles.successTitle}>Swap Request Submitted Successfully! 🎉</h6>
                <p style={styles.successMessage}>
                  Your swap request has been saved in our database. The product owner will be
                  notified and you'll receive an email confirmation shortly.
                </p>
                <small style={styles.successNote}>
                  <i className="ri-information-line me-1" aria-hidden="true"></i>
                  This modal will close automatically...
                </small>
              </div>
            </div>
          </Alert>
        )}

        {/* Show loading if product is null */}
        {!product ? (
          <div style={styles.loadingContainer}>
            <Spinner
              animation="border"
              variant="primary"
              aria-label="Loading product information"
            />
            <p className="mt-2 text-muted">Loading product information...</p>
          </div>
        ) : (
          <>
            {!showSuccess && !error ? (
              <>
                {/* Product You're Requesting */}
                <section aria-labelledby="requested-product-title">
                  <h6 id="requested-product-title" style={styles.sectionTitle}>
                    <i className="ri-shopping-bag-line me-2" aria-hidden="true"></i>
                    Product You're Requesting
                  </h6>

                  <div
                    style={styles.productCard}
                    role="article"
                    aria-label={`Product: ${product.name}`}
                  >
                    <Row className="align-items-center">
                      <Col xs={12}>
                        <h6 style={styles.productName}>{product.name || 'Product not available'}</h6>
                        <div style={styles.productDetails}>
                          {product.user_email && (
                            <>
                              <br />
                              <span className="text-muted">Owner: {product.user_email}</span>
                            </>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </section>

                {/* If not logged in, show 'Please login first' message instead of form */}
                {!isLoggedIn ? (
                  <Alert variant="warning" style={styles.alert}>
                    <div style={styles.errorContent}>
                      <i className="ri-information-line me-2" aria-hidden="true"></i>
                      <div>
                        <h6 style={styles.errorTitle}>Please Login First</h6>
                        <p style={styles.errorMessage}>
                          You need to login to your account before you can request a product swap.
                        </p>
                      </div>
                    </div>
                  </Alert>
                ) : isOwnProduct ? (
                  // Logged in BUT trying to request own product
                  <Alert variant="danger" style={styles.alert}>
                    <div style={styles.errorContent}>
                      <i className="ri-close-circle-line me-2" aria-hidden="true"></i>
                      <div>
                        <h6 style={styles.errorTitle}>Action Not Allowed</h6>
                        <p style={styles.errorMessage}>
                          You cannot request a swap for your own product.
                        </p>
                      </div>
                    </div>
                  </Alert>
                ) : (
                  <Form onSubmit={handleSubmit} noValidate>
                    {/* Your Email */}
                    <section aria-labelledby="contact-info-title">
                      <h6 id="contact-info-title" style={styles.sectionTitle}>
                        <i className="ri-mail-line me-2" aria-hidden="true"></i>
                        Your Contact Information
                      </h6>

                      <Form.Group className="mb-4">
                        <Form.Label htmlFor="user-email">Email Address *</Form.Label>
                        <Form.Control
                          id="user-email"
                          type="email"
                          placeholder="Logged-in user email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={isSubmitting}
                          readOnly
                          style={{
                            ...styles.formInput,
                            backgroundColor: '#f1f3f5',
                            cursor: 'not-allowed',
                          }}
                          aria-describedby="email-help"
                          autoComplete="email"
                        />
                        <Form.Text id="email-help" className="text-muted">
                          We'll notify you about the swap request status via email.
                        </Form.Text>
                      </Form.Group>
                    </section>

                    {/* Your Product for Swap (dropdown from your products) */}
                    <section aria-labelledby="swap-product-title">
                      <h6 id="swap-product-title" style={styles.sectionTitle}>
                        <i className="ri-exchange-box-line me-2" aria-hidden="true"></i>
                        Your Product for Swap *
                      </h6>

                      <Form.Group className="mb-4">
                        {productsLoading ? (
                          <div className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span className="text-muted">Loading your products...</span>
                          </div>
                        ) : userProducts.length === 0 ? (
                          <>
                            <Alert variant="info" style={styles.alert}>
                              <div style={styles.errorContent}>
                                <i className="ri-information-line me-2" aria-hidden="true"></i>
                                <div>
                                  <h6 style={styles.errorTitle}>No Products Found</h6>
                                  <p style={styles.errorMessage}>
                                    You don't have any verified products yet. Please add a product
                                    and wait for approval before requesting a swap.
                                  </p>
                                </div>
                              </div>
                            </Alert>
                          </>
                        ) : (
                          <>
                            <Form.Label htmlFor="swap-product-select">
                              Select Your Product *
                            </Form.Label>
                            <Form.Select
                              id="swap-product-select"
                              value={selectedProductId}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSelectedProductId(val);
                                const chosen = userProducts.find(
                                  (p) => String(p.id) === String(val)
                                );
                                setProductName(chosen ? chosen.name || '' : '');
                              }}
                              disabled={isSubmitting || productsLoading}
                              style={styles.formInput}
                              aria-describedby="product-help"
                            >
                              {userProducts.map((p) => (
                                <option key={p.id} value={String(p.id)}>
                                  {p.name || `Product #${p.id}`}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Text id="product-help" className="text-muted">
                              Only your approved (verified) products are shown here.
                            </Form.Text>
                            {productsError && (
                              <div
                                style={{
                                  color: '#dc3545',
                                  marginTop: '0.25rem',
                                  fontSize: '0.85rem',
                                }}
                              >
                                {productsError}
                              </div>
                            )}
                          </>
                        )}
                      </Form.Group>

                      {/* Product Preview */}
                      {productName.trim().length >= 2 && userProducts.length > 0 && (
                        <div style={styles.productPreview} aria-live="polite">
                          <h6 style={styles.previewTitle}>
                            <i
                              className="ri-checkbox-circle-line me-2"
                              style={{ color: '#28a745' }}
                              aria-hidden="true"
                            ></i>
                            Your Offer Preview:
                          </h6>
                          <div style={styles.previewCard}>
                            <Row className="align-items-center">
                              <Col xs={12}>
                                <strong style={styles.previewProductName}>{productName}</strong>
                                <br />
                                <small className="text-muted">
                                  You're offering this product in exchange for {product.name}
                                </small>
                              </Col>
                            </Row>
                          </div>
                        </div>
                      )}
                    </section>

                    {/* Action Buttons */}
                    <div style={styles.modalFooter}>
                      <Button
                        variant="outline-secondary"
                        onClick={handleClose}
                        style={styles.cancelBtn}
                        disabled={isSubmitting}
                        aria-label="Cancel swap request"
                      >
                        Cancel
                      </Button>

                      <Button
                        variant="primary"
                        type="submit"
                        disabled={
                          isSubmitting ||
                          !selectedProductId ||
                          productsLoading ||
                          userProducts.length === 0
                        }
                        style={styles.submitBtn}
                        aria-label={isSubmitting ? 'Submitting swap request' : 'Send swap request'}
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                              aria-hidden="true"
                            />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line me-2" aria-hidden="true"></i>
                            Submit Swap Request
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </>
            ) : (
              <div style={styles.autoCloseContainer}>
                <Spinner
                  animation="border"
                  variant={showSuccess ? 'success' : 'danger'}
                  aria-label={showSuccess ? 'Request completed successfully' : 'Request failed'}
                />
                <p className="mt-2 text-muted">
                  {showSuccess ? 'Closing...' : 'Closing '}
                </p>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

// ==================== Styles ====================
const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    textAlign: 'center',
  },
  autoCloseContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    textAlign: 'center',
  },
  modalHeader: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #e9ecef',
    padding: '1.5rem 1.5rem 1rem',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#405189',
  },
  modalBody: {
    padding: '0 1.5rem 1.5rem',
  },
  alert: {
    borderRadius: '8px',
    marginBottom: '1.5rem',
    border: 'none',
    fontSize: '0.9rem',
  },
  errorContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  errorTitle: {
    color: '#dc3545',
    fontWeight: '600',
    marginBottom: '0.5rem',
  },
  errorMessage: {
    color: '#721c24',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
  },
  errorNote: {
    color: '#6c757d',
    fontStyle: 'italic',
  },
  successAlert: {
    borderRadius: '8px',
    marginBottom: '1.5rem',
    border: 'none',
    backgroundColor: '#d1edff',
    borderLeft: '4px solid #28a745',
  },
  successContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  successTitle: {
    color: '#28a745',
    fontWeight: '600',
    marginBottom: '0.5rem',
  },
  successMessage: {
    color: '#155724',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
  },
  successNote: {
    color: '#6c757d',
    fontStyle: 'italic',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#405189',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
  },
  productCard: {
    backgroundColor: '#f8f9fa',
    padding: '1rem',
    borderRadius: '8px',
    border: '1px solid #e9ecef',   // ✅ FIXED HERE
    marginBottom: '1.5rem',
  },
  productName: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#0056b3',
    marginBottom: '0.5rem',
  },
  productDetails: {
    fontSize: '0.85rem',
    marginBottom: '0',
    lineHeight: '1.4',
  },
  formInput: {
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    padding: '0.75rem 1rem',
    fontSize: '0.9rem',
    transition: 'all 0.3s ease',
  },
  productPreview: {
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  previewTitle: {
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
  },
  previewCard: {
    backgroundColor: '#e7f3ff',
    padding: '1rem',
    borderRadius: '8px',
    border: '1px solid #b3d9ff',
  },
  previewProductName: {
    color: '#0056b3',
    fontSize: '1rem',
  },
  modalFooter: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e9ecef',
    marginTop: '1rem',
  },
  cancelBtn: {
    borderRadius: '6px',
    padding: '0.5rem 1.5rem',
    fontWeight: '500',
    minWidth: '80px',
  },
  submitBtn: {
    borderRadius: '6px',
    padding: '0.5rem 1.5rem',
    fontWeight: '600',
    backgroundColor: '#405189',
    borderColor: '#405189',
    color: '#fff',
    minWidth: '160px',
    transition: 'all 0.3s ease',
  },
};

export default RequestModal;
